import React, { Fragment } from 'react';
import { Printer, ArrowRight, UserX, Activity, BarChart3, TrendingUp, ShieldCheck, Eye, Rocket } from 'lucide-react';
const SYSTEM_STEPS = ['Attract', 'Engage', 'Screen', 'Qualify', 'Interview', 'Commit', 'Onboard', 'Integrate', 'Train', 'Launch', 'Retain'];
const PROBLEMS = [{
  icon: UserX,
  title: 'Wrong people in, right people out',
  desc: 'Late filtering, early drop-off, and fast churn drain every campaign.'
}, {
  icon: Activity,
  title: 'Leadership stuck fixing hires',
  desc: 'Time spent on unqualified interviews instead of building the team.'
}, {
  icon: BarChart3,
  title: 'No predictability, no scale',
  desc: 'Rollercoaster performance with no visibility and eroding culture.'
}];
const VALUE_PROPS = [{
  icon: TrendingUp,
  title: 'A predictable pipeline',
  desc: 'A steady flow of qualified candidates, month after month, instead of feast-or-famine hiring sprints.'
}, {
  icon: ShieldCheck,
  title: 'Quality at scale',
  desc: 'The right archetype screened in early and weak fits filtered out before they reach your team.'
}, {
  icon: Rocket,
  title: 'Leadership freed to build',
  desc: 'Your leaders coach and grow the team instead of scrambling to recruit and re-recruit.'
}, {
  icon: Eye,
  title: 'Full visibility and KPIs',
  desc: 'Live tracking across every stage so you always know what to expect and what to adjust.'
}];
const STATS = [{
  value: '5,000+',
  label: 'hires placed'
}, {
  value: '600+',
  label: 'monthly door-to-door hires'
}, {
  value: '21',
  label: 'D2D industries served'
}, {
  value: '15+',
  label: 'new market expansions'
}, {
  value: '60+',
  label: 'active partnerships'
}];
export function OnePager() {
  return <div className="onepager-print-root min-h-screen w-full bg-slate-200 py-6 px-3 sm:py-10 sm:px-6 print:p-0 print:bg-white" data-id="element-1105" data-mp="3jig1w19igc9h">
      {/* Action bar (screen only) */}
      <div className="no-print max-w-[8.5in] mx-auto mb-4 flex items-center justify-between" data-id="element-1106" data-mp="39t96ncibzq4r">
        <p className="text-sm text-slate-500" data-id="element-1107" data-mp="3evhxo3bn8i48">
          Print or save as PDF to send as a leave-behind.
        </p>
        <button onClick={() => window.print()} className="inline-flex items-center px-5 py-2.5 rounded-lg bg-[#163A5F] hover:bg-[#0f2a47] text-white font-semibold text-sm transition-colors shadow-sm" data-id="element-1108" data-mp="30w8klciqi44h">
          <Printer className="w-4 h-4 mr-2" data-id="element-1109" data-mp="1er0xhfv4mob" />
          Print / Save as PDF
        </button>
      </div>

      {/* The printable sheet */}
      <article className="onepager-sheet mx-auto w-full max-w-[8.5in] bg-white text-slate-800 shadow-xl rounded-lg overflow-hidden" data-id="element-1110" data-mp="3js8g2xax7pst">
        {/* Header band */}
        <header className="bg-[#163A5F] text-white px-8 sm:px-10 py-7 print:py-4 flex items-start justify-between" data-id="element-1111" data-mp="ff1synsws7b2">
          <div data-id="element-1112" data-mp="k5rxjytig64p">
            <div className="flex items-center gap-2.5" data-id="element-1113" data-mp="k6bizpiddcnh">
              <span className="text-3xl print:text-2xl font-extrabold tracking-tight" data-id="element-1114" data-mp="24vnmqtciwbw1">
                HIRE
              </span>
              <span className="h-6 w-px bg-white/30" data-id="element-1115" data-mp="1zvsvupsqzmaz" />
              <span className="text-sm font-medium text-[#7FE0C3] tracking-wide uppercase" data-id="element-1116" data-mp="1zuzuk4ck5p08">
                The HIRE Advantage
              </span>
            </div>
            <p className="mt-2 print:mt-1 text-sm text-slate-300 max-w-md leading-relaxed" data-id="element-1117" data-mp="29n5ovkqa8iil">
              Predictable recruiting systems for high-growth companies.
            </p>
          </div>
          <div className="text-right text-xs text-slate-300 leading-relaxed hidden sm:block" data-id="element-1118" data-mp="1gz9q8ah5kgj6">
            <p className="font-semibold text-white" data-id="element-1119" data-mp="1vw0i7yohypyr">d2dhire.com</p>
            <p className="text-[#7FE0C3] font-medium" data-id="element-1120" data-mp="1qvmn1h7zvai5">Predictable Recruiting</p>
          </div>
        </header>

        <div className="onepager-body px-8 sm:px-10 py-8 print:py-4 space-y-7 print:space-y-3.5" data-id="element-1121" data-mp="379miaevwabz2">
          {/* Hero */}
          <section data-id="element-1122" data-mp="32ajf6zzh6ftq">
            <h1 className="text-[1.7rem] sm:text-4xl print:text-[1.5rem] font-extrabold text-[#163A5F] leading-tight print:leading-[1.15]" data-id="element-1123" data-mp="32dfb6irriufj">
              Stop hiring. Start running a recruiting
              <span className="text-[#15b48a]" data-id="element-1124" data-mp="1gtk9kpo8no29"> system that scales.</span>
            </h1>
            <p className="mt-3 print:mt-1.5 text-[15px] sm:text-base print:text-[11.5px] text-slate-600 leading-relaxed max-w-3xl" data-id="element-1125" data-mp="1ktccomuv5208">
              High-growth companies don't get lucky with hiring. They run a
              repeatable system that turns recruiting into a predictable growth
              engine. We build and run that system for you, so headcount keeps
              pace with revenue without burning out your leaders.
            </p>
          </section>

          {/* Problem */}
          <section data-id="element-1126" data-mp="28jyjkat4sg8n">
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#15b48a] mb-3 print:mb-2" data-id="element-1127" data-mp="28mkmfx52u7kj">
              Where recruiting breaks
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 print:gap-2.5" data-id="element-1128" data-mp="2ijpak2ihgd3z">
              {PROBLEMS.map(p => <div key={p.title} className="rounded-lg border border-slate-200 bg-slate-50 p-4 print:p-2.5" data-id="element-1129" data-mp="2snw17aiqrr35">
                  <div className="flex items-center justify-center w-9 h-9 print:w-7 print:h-7 rounded-md bg-[#163A5F]/5 text-[#163A5F] mb-2.5 print:mb-1.5" data-id="element-1130" data-mp="2inn2nq1a1tkl">
                    <p.icon className="w-5 h-5 print:w-4 print:h-4" data-id="element-1131" data-mp="2ep643uvx9res" />
                  </div>
                  <h3 className="text-sm font-bold text-[#163A5F] mb-1 leading-snug" data-id="element-1132" data-mp="3bewxtrwe3ajj">
                    {p.title}
                  </h3>
                  <p className="text-[12.5px] print:text-[10.5px] text-slate-500 leading-relaxed" data-id="element-1133" data-mp="31mhfuul3elyn">
                    {p.desc}
                  </p>
                </div>)}
            </div>
          </section>

          {/* System */}
          <section data-id="element-1134" data-mp="3u5oeleipi37y">
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#15b48a] mb-3 print:mb-2" data-id="element-1135" data-mp="3pcv9uqn8muvv">
              The HIRE recruiting system
            </h2>
            <div className="rounded-lg border border-slate-200 p-4 print:p-2.5" data-id="element-1136" data-mp="1nftdc13kj8gc">
              <div className="flex flex-wrap items-center gap-y-2.5 print:gap-y-1.5" data-id="element-1137" data-mp="1sd32dfn1pi3n">
                {SYSTEM_STEPS.map((step, idx) => <Fragment key={step} data-id="element-1138" data-mp="1dnnp92gftb6f" data-mpi={idx}>
                    <span className="inline-flex items-center rounded-full bg-[#163A5F] text-white text-[11.5px] print:text-[10.5px] font-semibold px-3 py-1.5 print:px-2.5 print:py-1" data-id="element-1139" data-mp="1int8bu8n5pcw">
                      {step}
                    </span>
                    {idx < SYSTEM_STEPS.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-slate-300 mx-1.5 print:mx-1 flex-shrink-0" data-id="element-1140" data-mp="urfitvbouu9j" />}
                  </Fragment>)}
              </div>
              <p className="mt-3 print:mt-2 text-[12.5px] print:text-[10.5px] text-slate-500 leading-relaxed" data-id="element-1141" data-mp="2p458g0v0xhb4">
                Eleven engineered stages, from attracting the right candidates
                to retaining your best people, each one measured and tuned for
                predictable output.
              </p>
            </div>
          </section>

          {/* How we help grow predictably */}
          <section data-id="element-1142" data-mp="s6hf00mg2jww">
            <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-[#15b48a] mb-3 print:mb-2" data-id="element-1143" data-mp="s6zjfkj9g4fw">
              How we help you grow predictably
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 print:gap-x-5 print:gap-y-2.5" data-id="element-1144" data-mp="116cughn1912q">
              {VALUE_PROPS.map(v => <div key={v.title} className="flex items-start gap-3" data-id="element-1145" data-mp="x5158d82mhv2">
                  <div className="flex items-center justify-center w-9 h-9 print:w-7 print:h-7 rounded-md bg-[#15b48a]/10 text-[#15b48a] flex-shrink-0" data-id="element-1146" data-mp="1b40u9lzhwyrr">
                    <v.icon className="w-5 h-5 print:w-4 print:h-4" data-id="element-1147" data-mp="3cwf8a9yx5j2w" />
                  </div>
                  <div data-id="element-1148" data-mp="3cy9tzqi0lf81">
                    <h3 className="text-sm font-bold text-[#163A5F] leading-snug" data-id="element-1149" data-mp="37xvlk7ci439h">
                      {v.title}
                    </h3>
                    <p className="text-[12.5px] print:text-[10.5px] text-slate-500 leading-relaxed" data-id="element-1150" data-mp="2m3n75g1c1spw">
                      {v.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </section>

          {/* Proof */}
          <section className="rounded-lg bg-[#163A5F] px-6 py-5 print:py-3.5 print:px-5" data-id="element-1151" data-mp="30s181lf6v73t">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 print:gap-2" data-id="element-1152" data-mp="2363wk7z8821d">
              {STATS.map(s => <div key={s.label} className="text-center" data-id="element-1153" data-mp="2856pwv5ugjti">
                  <div className="text-2xl sm:text-3xl print:text-xl font-extrabold text-[#7FE0C3] leading-none" data-id="element-1154" data-mp="2d3rs304npdkl">
                    {s.value}
                  </div>
                  <div className="mt-1.5 print:mt-1 text-[10.5px] print:text-[9px] uppercase tracking-wide text-slate-300 leading-tight" data-id="element-1155" data-mp="2i0i5tgsznx2u">
                    {s.label}
                  </div>
                </div>)}
            </div>
            <p className="mt-4 print:mt-2.5 text-center text-[12.5px] print:text-[10.5px] text-slate-300 leading-relaxed" data-id="element-1156" data-mp="1huwmmsb2rfrr">
              Real results across 21 door-to-door industries, from solar,
              roofing, and pest control to windows, life insurance, and home
              services, in single offices and multi-market rollouts alike.
            </p>
          </section>
        </div>
      </article>
    </div>;
}